import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import client from "../../../api/client";
import FeatureFeedbackBox from "../../Generic/FeatureFeedbackBox";
import { fmtDay } from "../home/time";
import Icon from "./Icon";
import PageFrame, { Loading } from "./PageFrame";

const BACKEND = process.env.REACT_APP_DOMAIN_URL || "";
const band = (s) => (s >= 80 ? "Strong, ready to send" : s >= 60 ? "Good, with room to grow" : "Needs work before you apply");
const ringColor = (s) => (s >= 80 ? "var(--good)" : s >= 60 ? "var(--brand)" : "var(--warn)");

/** Prototype `.drop` uploader: PDF or DOCX up to 5 MB, sent to /me/cv-submissions. */
function Uploader({ title, left, max, onUploaded }) {
  const input = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("That file is over 5 MB. Save a smaller PDF or DOCX and try again.");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("cv", file);
      const res = await client.post("/me/cv-submissions", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("CV uploaded. We're reviewing it now.");
      onUploaded(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.message || "We couldn't upload your CV. Please try again.");
    }
    setBusy(false);
    return undefined;
  };
  if (left <= 0) return <section className="complete-box"><div><strong>You've used all {max} CV reviews</strong><small>Your latest review and revised CV stay here.</small></div></section>;
  return (
    <section className="drop">
      <Icon name="upload" size={28} />
      <strong>{title}</strong>
      <span>PDF or DOCX · up to 5 MB · {left} of {max} reviews left</span>
      <input ref={input} type="file" accept=".pdf,.docx" hidden onChange={pick} />
      <button className="cbtn go" type="button" onClick={() => input.current?.click()} disabled={busy}>{busy ? "Uploading…" : "Choose file"}</button>
    </section>
  );
}

function Working({ title, text }) {
  return <section className="empty"><div className="spinner" aria-hidden="true" /><h3>{title}</h3><p>{text}</p></section>;
}

function Points({ title, items, tone }) {
  if (!items?.length) return null;
  return (
    <section className="card">
      <div className="card-h"><h3>{title}</h3><span className={`st ${tone}`}>{items.length}</span></div>
      {items.map((t, i) => <div key={i} className="cv-part"><small style={{ color: "var(--text)", fontSize: 14 }}>{t}</small></div>)}
    </section>
  );
}

/** Follow-up questions that turn the review into a revised CV (first draft or a redraft). */
function Questions({ submission, redraft, initial, onDone, onCancel }) {
  const qs = submission.followup_questions || [];
  const [answers, setAnswers] = useState(initial?.followup_answers || {});
  const [sector, setSector] = useState(initial?.target_sector || "");
  const [busy, setBusy] = useState(false);
  const send = async (e) => {
    e.preventDefault();
    if (qs.some((q) => !answers[q.id]?.trim())) return toast.error("Answer every question first. They shape your revised CV.");
    setBusy(true);
    try {
      await client.post(`/me/cv-submissions/${submission.id}/revisions`, { answers, target_sector: sector.trim() || null });
      toast.success(redraft ? "Redrafting your CV…" : "Drafting your revised CV…");
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.message || "We couldn't draft your revised CV. Please try again.");
    }
    setBusy(false);
    return undefined;
  };
  return (
    <section className="card">
      <div className="card-h"><h3>{redraft ? "Redraft your CV" : "Get a revised CV"}</h3></div>
      <p style={{ color: "var(--muted)" }}>{redraft ? "Change your answers, or aim this draft at a different role or sector." : "Answer a few questions about the gaps in your review and we'll draft a stronger CV for you."}</p>
      <form className="form" onSubmit={send}>
        {qs.map((q) => (
          <label key={q.id} htmlFor={`cvq-${q.id}`}>{q.question}
            <textarea id={`cvq-${q.id}`} rows={2} value={answers[q.id] || ""} onChange={(e) => setAnswers({ ...answers, [q.id]: e.target.value })} />
          </label>
        ))}
        <label htmlFor="cv-target">Roles or industries you're targeting<input id="cv-target" value={sector} onChange={(e) => setSector(e.target.value)} placeholder="e.g. Data Analyst, Fintech, Product Management" /><small>Optional. We tailor the draft to these roles.</small></label>
        <div className="cta-row">
          <button className="cbtn go" type="submit" disabled={busy}>{busy ? "Drafting…" : redraft ? "Redraft my CV" : "Draft my revised CV"}</button>
          {onCancel && <button className="cbtn quiet" type="button" onClick={onCancel} disabled={busy}>Cancel</button>}
        </div>
      </form>
    </section>
  );
}

function RevisedCv({ submission, revision, redraftsLeft, maxRedrafts, onRedraft }) {
  const [busy, setBusy] = useState(null);
  const base = `${BACKEND}/api/me/cv-submissions/${submission.id}/revisions/${revision.id}`;
  const fetchBlob = async (path) => {
    const res = await fetch(`${base}/${path}`, { headers: { Authorization: client.defaults.headers.Authorization || "" } });
    if (!res.ok) throw new Error();
    return URL.createObjectURL(await res.blob());
  };
  const download = async (format) => {
    setBusy(format);
    try {
      const url = await fetchBlob(`download/${format}`);
      const a = document.createElement("a");
      a.href = url;
      a.download = `revised-cv.${format}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("We couldn't download your revised CV. Please try again.");
    }
    setBusy(null);
  };
  const preview = async () => {
    const tab = window.open("", "_blank");
    setBusy("preview");
    try {
      const url = await fetchBlob("preview");
      if (tab) tab.location.href = url; else window.open(url, "_blank");
    } catch {
      tab?.close();
      toast.error("We couldn't open the preview. Please try again.");
    }
    setBusy(null);
  };
  return (
    <section className="card">
      <div className="card-h"><h3>Your revised CV</h3><span className="st done">Ready</span></div>
      <div className="file">
        <span className="ico doc"><Icon name="files" /></span>
        <div><strong>Revised CV</strong><small>{revision.target_sector ? `Aimed at ${revision.target_sector}` : "Drafted from your review and answers"}</small></div>
        <span style={{ display: "flex", gap: 4 }}>
          <button className="cbtn quiet" type="button" onClick={preview} disabled={!!busy}>{busy === "preview" ? "Opening…" : "Preview"}</button>
        </span>
      </div>
      <div className="cta-row">
        <button className="cbtn go" type="button" onClick={() => download("pdf")} disabled={!!busy}>{busy === "pdf" ? "Downloading…" : "Download PDF"}</button>
        <button className="cbtn quiet" type="button" onClick={() => download("docx")} disabled={!!busy}>{busy === "docx" ? "Downloading…" : "Download DOCX"}</button>
        <button className="link-btn" type="button" onClick={onRedraft} disabled={redraftsLeft <= 0}>{redraftsLeft > 0 ? `Not quite right? Redraft it (${redraftsLeft} of ${maxRedrafts} left) ›` : `All ${maxRedrafts} redrafts used`}</button>
      </div>
    </section>
  );
}

function History({ history }) {
  const scored = history.filter((h) => h.status === "reviewed");
  if (scored.length < 2) return null;
  return (
    <section className="card">
      <div className="card-h"><h3>Revision history</h3></div>
      {scored.map((h, i) => {
        const prev = scored[i + 1];
        const delta = prev ? h.ats_score - prev.ats_score : null;
        return (
          <div key={h.id} className="file">
            <span className="ico doc"><Icon name="files" /></span>
            <div><strong>CV upload {scored.length - i}</strong><small>{fmtDay(h.reviewed_at || h.created_at)} · score {h.ats_score}</small></div>
            {i === 0 ? <span className="st done">Latest</span> : delta != null ? <span className="st open">{delta >= 0 ? `+${delta}` : delta}</span> : <span />}
          </div>
        );
      })}
    </section>
  );
}

/** CV reviewer (prototype cvPage) on the real review flow: upload, score, feedback, revised CV, redrafts. */
export default function CvReviewerPage() {
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [redrafting, setRedrafting] = useState(false);

  const refresh = useCallback(() => client.get("/me/cv-submissions/latest").then((r) => { setData(r.data); setFailed(false); }).catch(() => setFailed(true)), []);
  useEffect(() => { refresh(); }, [refresh]);

  const sub = data?.data;
  const revision = sub?.revisions?.[0] || null;
  const busy = sub?.status === "processing" || revision?.status === "pending";
  useEffect(() => {
    if (!busy) return undefined;
    const t = setInterval(refresh, 4000);
    return () => clearInterval(t);
  }, [busy, refresh]);

  const head = (intro) => ({ title: "CV reviewer", kicker: "Career", intro });
  if (failed) return <PageFrame {...head()}><section className="complete-box"><div><strong>We couldn't load your CV review</strong><small>Check your connection and try again.</small></div><button className="cbtn quiet" type="button" onClick={refresh}>Try again</button></section></PageFrame>;
  if (!data) return <PageFrame {...head()}><Loading /></PageFrame>;
  if (!data.enrolled) {
    return (
      <PageFrame {...head()}>
        <section className="empty"><div className="empty-ico"><Icon name="doc" size={30} /></div><h3>CV reviews come with your course</h3><p>Students in a cohort get their CV scored, with feedback and a revised draft. Enroll in a course to use it.</p></section>
      </PageFrame>
    );
  }

  const max = data.max_uploads ?? 3;
  const left = Math.max(0, max - (data.uploads_used ?? 0));
  const redraftsLeft = Math.max(0, (data.max_redrafts ?? 3) - (data.redrafts_used ?? 0));
  const onUploaded = () => { setUploading(false); refresh(); };
  const history = data.history || [];

  if (!sub || (sub.status === "failed" && !uploading)) {
    return (
      <PageFrame {...head()}>
        {sub?.status === "failed" && <div className="notice"><Icon name="info" size={18} /><div><strong>We couldn't review that file</strong><span>{sub.failure_reason}</span></div></div>}
        {!sub && (
          <section className="empty">
            <div className="empty-ico"><Icon name="doc" size={30} /></div>
            <h3>Get your CV ready for tech roles</h3>
            <p>Upload your CV and get a score with feedback, then a revised draft tailored to the roles you want. Fix it, upload again, and watch your score improve.</p>
            <ol className="empty-steps">
              <li><b>1</b><div><strong>Upload your CV</strong><small>PDF or DOCX, up to 5 MB</small></div></li>
              <li><b>2</b><div><strong>Get your review</strong><small>Usually ready in a minute or two</small></div></li>
              <li><b>3</b><div><strong>Revise and resubmit</strong><small>Each upload is scored, so you can see progress</small></div></li>
            </ol>
          </section>
        )}
        <Uploader title={sub ? "Upload a text-based PDF or DOCX" : "Upload your CV to get started"} left={left} max={max} onUploaded={onUploaded} />
        <FeatureFeedbackBox feature="cv_review" />
      </PageFrame>
    );
  }

  if (sub.status === "processing") {
    return <PageFrame {...head()}><Working title="Reviewing your CV" text="This usually takes a minute or two. You can leave this page; your review will be here when you come back." /></PageFrame>;
  }

  return (
    <PageFrame {...head("Your latest review. Upload a revised CV any time to see your new score.")}>
      {uploading ? (
        <>
          <Uploader title="Upload your revised CV. We'll score it again." left={left} max={max} onUploaded={onUploaded} />
          <div className="cta-row"><button className="cbtn quiet" type="button" onClick={() => setUploading(false)}>Cancel</button></div>
        </>
      ) : (
        <section className="score">
          <div className="ring" style={{ background: `conic-gradient(${ringColor(sub.ats_score)} 0 ${sub.ats_score}%, var(--line) 0)` }}><b>{sub.ats_score}</b></div>
          <div>
            <h3>{band(sub.ats_score)}</h3>
            <p style={{ color: "var(--muted)" }}>ATS score out of 100 · reviewed {fmtDay(sub.reviewed_at || sub.created_at)}{revision?.target_sector ? ` · targeting ${revision.target_sector}` : ""}</p>
            <div className="cta-row" style={{ marginTop: 10 }}>
              {left > 0 ? <button className="cbtn go" type="button" onClick={() => setUploading(true)}>Upload revised CV</button> : <small>All {max} reviews used</small>}
              {left > 0 && <small>{left} of {max} reviews left</small>}
            </div>
          </div>
        </section>
      )}

      <Points title="What's working" items={sub.strengths} tone="done" />
      <Points title="What to fix" items={sub.gaps} tone="due" />
      <Points title="Suggested edits" items={sub.suggested_edits} tone="open" />

      {!revision ? <Questions submission={sub} onDone={refresh} />
        : revision.status === "pending" ? <Working title="Drafting your revised CV" text="This usually takes a minute or two. You can leave this page; your draft will be here when you come back." />
        : revision.status === "failed" ? (
          <>
            <div className="notice"><Icon name="info" size={18} /><div><strong>We couldn't draft your revised CV</strong><span>{revision.failure_reason || "Something went wrong. Please try again."}</span></div></div>
            <Questions submission={sub} initial={revision} onDone={refresh} />
          </>
        ) : redrafting ? <Questions submission={sub} redraft initial={revision} onDone={() => { setRedrafting(false); refresh(); }} onCancel={() => setRedrafting(false)} />
        : <RevisedCv submission={sub} revision={revision} redraftsLeft={redraftsLeft} maxRedrafts={data.max_redrafts ?? 3} onRedraft={() => setRedrafting(true)} />}

      <History history={history} />
      <FeatureFeedbackBox feature="cv_review" />
    </PageFrame>
  );
}
